//@flow
import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import theme from './Statistic.scss';

import { DivLoader, TooltipDiv } from 'common/components/core/wrappers/Div';
import TextColor from 'common/components/core/colors/text.scss';
import { NumberUtils } from 'common/utils';
import StringUtils from 'common/utils/StringUtils';
import { includes } from 'lodash';
import BackgroundColor from 'common/components/core/colors/background.scss';
import { MlFontIconLoader } from 'common/components/core/loader';
import { customEditableFieldFactory } from 'common/components/core/common/CustomEditableField';
import { EnhancedInput } from 'common/components/core/input';
import coreIds from 'common/tests/core/coreIds.json';

export const StatTypes = {
    Line: 'LINE',
    Icon: 'ICON'
};

type Props = {
    theme: Object,
    value?: string | number,
    totalValue?: string | number,
    className?: string,
    unit?: string,
    smallStat?: boolean,
    tooltip?: string,
    tooltipDelay?: string | number,
    title?: string,
    statType?: string,
    invert?: boolean,
    invertTooltip?: boolean,
    forceShowTooltip?: boolean,
    formatValue?: boolean,
    titleColor?: string,
    reversed?: boolean,
    backgroundColor?: string,
    icon?: string,
    pluralizeUnit?: boolean,
    valueType?: string,
    tooltipClassName?: string,
    titleColor?: string,
    valueColor?: string,
    uppercase?: boolean,
    testId?: string
};

export const TitleComponent = ({
    theme,
    className,
    title,
    uppercase,
    isLoading,
    titleColor,
    'data-test-id': dataTestId
}) => {
    if (isLoading) {
        return (
            <DivLoader loading placeholderLoaderClassName={theme.titleLoader} />
        );
    }
    return title ? (
        <div
            data-test-id={dataTestId}
            className={classnames(className, {
                [theme.uppercase]: uppercase,
                [TextColor[titleColor]]: TextColor[titleColor]
            })}
        >
            {title}
        </div>
    ) : null;
};

const ValueComponent = ({
    theme,
    value,
    totalValue,
    valueColor,
    isLoading,
    'data-test-id': dataTestId
}) => {
    return isLoading ? (
        <DivLoader loading placeholderLoaderClassName={theme.loader} />
    ) : (
        <div
            data-test-id={dataTestId}
            className={classnames(theme.value, {
                [TextColor[valueColor]]: TextColor[valueColor]
            })}
        >
            <span className={theme.valueInner}>
                <span className={theme.valueSpan}>{value}</span>
                {StringUtils.isEmpty(totalValue) ? null : (
                    <span
                        className={theme.totalValue}
                    >{` /${totalValue}`}</span>
                )}
            </span>
        </div>
    );
};

const EditableValueComponent = customEditableFieldFactory({
    ActiveFieldComponent: EnhancedInput,
    InactiveFieldComponent: ValueComponent
});

const UnitComponent = ({
    theme,
    unit,
    value,
    pluralizeUnit,
    isLoading,
    'data-test-id': dataTestId
}) => {
    const pluralizedUnit = StringUtils.pluralize(unit, value, null, false);

    if (isLoading) {
        return <DivLoader loading placeholderLoaderClassName={theme.loader} />;
    }
    return unit ? (
        <span data-test-id={dataTestId} className={theme.unit}>
            &nbsp;
            {pluralizeUnit ? pluralizedUnit : unit}
        </span>
    ) : null;
};

const DefaultSymbol = ({ theme, symbol }) => {
    return symbol ? <span className={theme.statSymbol}>{symbol}</span> : null;
};

const statisticFactory = ({
    Title = TitleComponent,
    Value = ValueComponent,
    Unit = UnitComponent,
    Symbol = DefaultSymbol,
    options = { themed: true }
}) => {
    class Statistic extends PureComponent<Props, void> {
        static defaultProps = {
            invertTooltip: true
        };

        getTooltipContent = () => {
            const { testId, tooltip } = this.props;
            if (!tooltip) {
                return null;
            }

            if (testId && typeof tooltip === 'string') {
                const tooltipTestId = `${coreIds.statistic.tooltip}-${testId}`;
                return <span data-test-id={tooltipTestId}>{tooltip}</span>;
            }
            return tooltip;
        };

        render() {
            const {
                isLoading,
                theme,
                value,
                totalValue,
                className,
                unit,
                smallStat,
                tooltipDelay,
                tooltipPosition,
                title,
                statType,
                invert,
                invertTooltip,
                forceShowTooltip,
                formatValue,
                backgroundColor,
                icon,
                pluralizeUnit,
                symbol,
                tooltipClassName,
                style,
                reversed,
                uppercase,
                disablePointerEvents,
                roundOff,
                testId,
                isEditingEnabled,
                ...others
            } = this.props;
            let displayValue = value;
            if ((formatValue || roundOff) && !StringUtils.isEmpty(value)) {
                displayValue = NumberUtils.formatNumber(value, roundOff, 2);
            }
            const statTypeClass =
                statType && includes(StatTypes, statType)
                    ? theme[`${statType.toLowerCase()}Stat`]
                    : null;
            const tooltipContent = this.getTooltipContent();
            const shouldRenderStat =
                !StringUtils.isEmpty(displayValue) ||
                !StringUtils.isEmpty(totalValue) ||
                forceShowTooltip ||
                isLoading;

            const dataTestId = testId
                ? `${coreIds.statistic.item}-${testId}`
                : coreIds.statistic.item;

            return shouldRenderStat ? (
                <TooltipDiv
                    style={style}
                    smallTooltip
                    invert={invertTooltip}
                    tooltip={tooltipContent}
                    className={classnames(
                        theme.statistic,
                        className,
                        statTypeClass,
                        {
                            [theme.editableStat]: isEditingEnabled,
                            [theme.smallStat]: smallStat,
                            [theme.invert]: invert,
                            [BackgroundColor[backgroundColor]]: backgroundColor,
                            [theme.reversed]: reversed
                        }
                    )}
                    tooltipClassName={tooltipClassName}
                    tooltipDelay={tooltipDelay}
                    tooltipPosition={tooltipPosition}
                    theme={theme}
                    testId={testId}
                    data-test-id={dataTestId}
                >
                    {icon ? (
                        <MlFontIconLoader
                            loading={isLoading}
                            className={classnames(theme.statIcon, {
                                [theme.disablePointerEvents]: disablePointerEvents
                            })}
                            iconClass={icon}
                        />
                    ) : null}
                    <div
                        className={classnames(theme.valueContainer, {
                            [theme.uppercase]: uppercase,
                            [theme.disablePointerEvents]: disablePointerEvents
                        })}
                    >
                        <Value
                            data-test-id={coreIds.statistic.value}
                            isLoading={isLoading}
                            value={displayValue}
                            totalValue={totalValue}
                            pluralizeUnit={pluralizeUnit}
                            unit={unit}
                            title={title}
                            theme={theme}
                            isEditingEnabled={isEditingEnabled}
                            {...others}
                        />
                        <Unit
                            data-test-id={coreIds.statistic.unit}
                            isLoading={isLoading}
                            value={displayValue}
                            totalValue={totalValue}
                            unit={unit}
                            pluralizeUnit={pluralizeUnit}
                            title={title}
                            theme={theme}
                            {...others}
                        />
                        <Symbol theme={theme} symbol={symbol} {...others} />
                    </div>
                    <Title
                        data-test-id={coreIds.statistic.title}
                        isLoading={isLoading}
                        className={classnames(theme.title, {
                            [theme.disablePointerEvents]: disablePointerEvents
                        })}
                        title={title}
                        value={value}
                        totalValue={totalValue}
                        pluralizeUnit={pluralizeUnit}
                        unit={unit}
                        theme={theme}
                        uppercase={uppercase}
                        {...others}
                    />
                </TooltipDiv>
            ) : null;
        }
    }
    return options.themed ? themr('Statistic', theme)(Statistic) : Statistic;
};

const UnthemedStatistic = statisticFactory({ options: { themed: false } });
const ThemedStatistic = statisticFactory({});

const EditableStatisticFactoryView = statisticFactory({
    Value: EditableValueComponent
});

export default ThemedStatistic;
export { EditableStatisticFactoryView as EditableStatistic };
export { ThemedStatistic as Statistic, statisticFactory, UnthemedStatistic };
